/** `YYYY-MM` arithmetic and month names for `NxMonthPicker`. */

const MONTH_PATTERN = /^(\d{4})-(\d{2})$/;

/**
 * Splits a `YYYY-MM` value; both parts are `NaN` when the value is malformed.
 *
 * ⚠️ The old `?? NaN` looked like a guard and was none: `Number('kaputt')` is
 * already `NaN`, not `undefined`, so it passed straight through and
 * `shiftYear('kaputt', 1)` composed the string `"NaN-NaN"`. The pattern decides
 * now, and callers that build a new value from the result must check
 * `Number.isFinite` rather than print the result of `NaN` arithmetic.
 */
export function parseMonth(value: string): { year: number; month: number } {
  const match = MONTH_PATTERN.exec(value);
  if (!match) return { year: NaN, month: NaN };
  return { year: Number(match[1]), month: Number(match[2]) };
}

/** Joins a year and a month (1 = January) back into `YYYY-MM`. */
export function formatMonth(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`;
}

/**
 * The same month `delta` years later (negative shifts back). A value that is not
 * a `YYYY-MM` month comes back unchanged — there is no year in it to shift, and
 * returning `"NaN-NaN"` would hand the caller a string that looks like a month.
 */
export function shiftYear(value: string, delta: number): string {
  const { year, month } = parseMonth(value);
  if (!Number.isFinite(year)) return value;
  return formatMonth(year + delta, month);
}

/** The twelve month names of the locale, January first. */
export function monthLabels(locale = 'de-DE'): string[] {
  const format = new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' });
  return Array.from({ length: 12 }, (_, i) => format.format(new Date(Date.UTC(2000, i, 1))));
}

/**
 * Whether a `YYYY-MM` value lies within the optional bounds. The format sorts
 * like a date, so a string compare is the whole test.
 */
export function isWithin(value: string, min?: string, max?: string): boolean {
  if (min && value < min) return false;
  if (max && value > max) return false;
  return true;
}

/** The month `now` falls into, as `YYYY-MM`. */
export function todayMonth(now = new Date()): string {
  return formatMonth(now.getFullYear(), now.getMonth() + 1);
}
